/**
 * PRODUCTS STORE
 * Store pour la gestion des produits et abonnements
 */

import { EventDrivenStore } from '@core/store/eventDrivenStore';
import requestManager from '@core/backend/api/requestManager';
import { serializeProducts } from '@core/backend/serializers/productSerializer';
import constants from '@core/utils/constants';

class ProductsStore extends EventDrivenStore {
  constructor() {
    super('products', {
      subscriptions: [],
      giftCards: [],
      allProducts: [],
      featuredProducts: [],
      loading: false,
      error: null,
      lastFetch: 0,
    });
  }

  setupEvents() {
    // Écouter les événements de rafraîchissement
    this.on('products:refresh', async (force = false) => {
      await this.fetchProducts(force);
    });

    // Écouter les événements de recherche
    this.on('products:search', (query) => {
      const results = this.search(query);
      window.$bus?.emit('products:search:results', results);
    });

    // Écouter les événements de filtre par catégorie
    this.on('products:filter:category', (category) => {
      const results = this.filterByCategory(category);
      window.$bus?.emit('products:filter:results', results);
    });

    // Charger les produits immédiatement après la configuration des événements
    // On utilise setTimeout pour s'assurer que le bus est prêt
    setTimeout(() => {
      this.fetchProducts().catch(error => {
        console.error('Erreur initialisation produits:', error);
      });
    }, 100);
  }

  /**
   * Charge tous les produits
   */
  async fetchProducts(force = false) {
    // Éviter le rechargement trop fréquent (cache de 30 secondes)
    if (!force && Date.now() - this.state.lastFetch < 30000) {
      return this.state.allProducts;
    }

    this.state.loading = true;
    this.state.error = null;

    try {
      // Charger en parallèle
      const [subscriptionsData, giftCardsData] = await Promise.all([
        requestManager.getSubscriptions(),
        requestManager.getGiftCards(),
      ]);

      // Serializer les données
      const subscriptions = serializeProducts(subscriptionsData, 'subscription') || [];
      const giftCards = serializeProducts(giftCardsData, 'giftcard') || [];

      this.state.subscriptions = subscriptions;
      this.state.giftCards = giftCards;
      this.state.allProducts = [...subscriptions, ...giftCards];

      // Produits mis en avant (premiers de chaque catégorie)
      this.state.featuredProducts = [
        subscriptions.slice(0, 5),
        giftCards.slice(0, 3),
      ].flat();

      this.state.lastFetch = Date.now();

      window.$bus?.emit('products:loaded', this.state);
      return this.state.allProducts;
    } catch (error) {
      console.error('Erreur chargement produits:', error);
      this.state.error = error.message;
      window.$bus?.emit('products:error', error);
      throw error;
    } finally {
      this.state.loading = false;
    }
  }

  /**
   * Récupère les abonnements
   */
  async fetchSubscriptions() {
    this.state.loading = true;
    try {
      const data = await requestManager.getSubscriptions();
      this.state.subscriptions = serializeProducts(data, 'subscription') || [];
      return this.state.subscriptions;
    } catch (error) {
      console.error('Erreur chargement abonnements:', error);
      throw error;
    } finally {
      this.state.loading = false;
    }
  }

  /**
   * Récupère les cartes cadeaux
   */
  async fetchGiftCards() {
    this.state.loading = true;
    try {
      const data = await requestManager.getGiftCards();
      this.state.giftCards = serializeProducts(data, 'giftcard') || [];
      return this.state.giftCards;
    } catch (error) {
      console.error('Erreur chargement cartes cadeaux:', error);
      throw error;
    } finally {
      this.state.loading = false;
    }
  }

  /**
   * Récupère un produit par ID
   */
  async fetchProduct(productId) {
    this.state.loading = true;
    try {
      const data = await requestManager.getProduct(productId);
      return serializeProducts([data])[0];
    } catch (error) {
      console.error('Erreur chargement produit:', error);
      throw error;
    } finally {
      this.state.loading = false;
    }
  }

  /**
   * Recherche des produits
   */
  search(query) {
    if (!query || query.trim() === '') {
      return this.state.allProducts;
    }

    const lowerQuery = query.toLowerCase();
    return this.state.allProducts.filter(product =>
      product.name?.toLowerCase().includes(lowerQuery) ||
      product.title?.toLowerCase().includes(lowerQuery) ||
      product.description?.toLowerCase().includes(lowerQuery)
    );
  }

  /**
   * Filtre les produits par catégorie
   */
  filterByCategory(category) {
    if (category === 'all' || !category) {
      return this.state.allProducts;
    }
    return this.state.allProducts.filter(product => product.category === category);
  }

  /**
   * Récupère les produits mis en avant
   */
  getFeaturedProducts(limit = 10) {
    return this.state.featuredProducts.slice(0, limit);
  }
}

export default ProductsStore;
